<script setup>
import {reactive, ref} from 'vue'
import {Feedback, State, can, clone, getModels, go} from '@/paks/vu-app'
import DisplayEdit from './DisplayEdit.vue'
import {getDisplay, persistDisplay, uploadAsset} from './BoardUtils'
import Json5 from 'json5'

const Fields = [
    {name: 'apply', title: 'Select', icon: '$click', width: '5%'},
    {name: 'edit', icon: '$edit', width: '5%'},
    {name: 'name', width: '30%'},
    {name: 'expression', title: 'Matching Expression', format: (v) => (v == 'true' ? 'Always Match' : v)},
]

// const emit = defineEmits(['input'])

const page = reactive({
    builder: State.config.builder,
    deploying: false,
    display: null,
    fields: Fields,
    select: {
        actions: {Add: 0, Edit: 1, Delete: 2, Export: 1},
        multi: true,
    },
    showEdit: false,

    files: null,
    import: null,
    importing: false,
    showImport: false,
    upload: true,
})

const table = ref(null)
const confirm = ref(null)

async function clicked({action, item, items, column, rec, row}) {
    if (action == 'add') {
        if (!can('admin')) {
            return
        }
        editDisplay({})
    } else if (action == 'edit' || (action == 'cell' && column.name == 'edit')) {
        if (can('admin')) {
            editDisplay(item)
        }
    } else if (action == 'cell') {
        if (column.name == 'apply') {
            let display = getDisplay(item.name)
            State.app.setDisplay(display)
            go()
        }
    } else if (action == 'export') {
        await exportApp()
    }
}

async function editDisplay(display) {
    if (display) {
        page.showEdit = true
        if (display.name) {
            page.existing = true
        } else {
            // Clone the standard display
            display = clone(State.app.standardDisplay.displays[0])
            display.name = null
            display.expression = null
        }
        page.display = display
    } else {
        page.showEdit = false
        page.display = null
    }
}

async function deployDisplay() {
    page.deploying = true
    await persistDisplay()
    Feedback.info('Please wait a minute for the app to be deployed')
    page.deploying = false
}

/*
    Export the the apps display.json5
 */
async function exportApp() {
    let fullDisplay = State.app.fullDisplay
    let data = Json5.stringify(fullDisplay, null, 4)
    let blob = new Blob([data], {type: 'text/plain'})
    let link = document.createElement('a')
    link.download = 'display.json5'
    if (window.webkitURL != null) {
        link.href = window.webkitURL.createObjectURL(blob)
    } else {
        link.href = window.URL.createObjectURL(blob)
        link.style.display = 'none'
        document.body.appendChild(link)
    }
    link.click()
    // emit('input')
}

function showImport() {
    page.showImport = true
    page.importing = false
}

async function importDisplay() {
    let {Manager} = getModels()

    page.saving = true
    page.importing = true
    if (!page.import) return null

    try {
        let text = await page.import.text()
        text = text.replace(/^export default/, '')
        const display = Json5.parse(text)
        let manager = await Manager.get({id: State.app.app.id})
        let data = text
        if (text.indexOf('export default') != 0) {
            data = 'export default ' + JSON.stringify(display, null, 4) + '\n'
        }
        await uploadAsset(manager, 'theme/display.js', data)
        Feedback.info('Please wait a minute for the app to be deployed')
        State.app.setFullDisplay(display)
    } catch (err) {
        Feedback.error('Display contains invalid JSON')
        throw new Error(`${page.import.name} contains invalid JSON`)
    } finally {
        page.saving = false
        page.importing = false
        page.showImport = false
    }
}
</script>

<template>
    <div class="view page display-list">
        <vu-help url="/doc/ui/designer/display-list.html" />
        <vu-table
            :data="State.app.fullDisplay.displays"
            :fields="page.fields"
            :select="page.select"
            name="display"
            nodata=" "
            options="filter,toolbar"
            ref="table"
            sort="name:asc"
            width="100%"
            @click="clicked">
            <template v-slot:more="props">
                <v-btn
                    class="mr-4"
                    v-if="State.app.needs.deploy"
                    color="primary"
                    :loading="page.deploying"
                    @click="deployDisplay()">
                    Deploy
                </v-btn>
                <v-btn class="mr-4" v-if="can('admin')" color="primary" @click="editDisplay({})">Add</v-btn>
                <!--
                <v-btn size="small" color="primary" class="mr-2" @click="exportApp">Export</v-btn>
                -->
                <v-btn color="error" @click="showImport">Import</v-btn>
            </template>
        </vu-table>

        <vu-panel v-model="page.showEdit" :widths="['650px']" @close="editDisplay">
            <DisplayEdit :display="page.display" @close="editDisplay" />
        </vu-panel>
    </div>
    <v-dialog v-model="page.showImport" width="550" content-class="import-dialog">
        <v-card class="pb-4">
            <v-card-title class="headline" primary-title>Import Display</v-card-title>
            <v-card-text class="mt-4 px-4">
                <label>Replace the display definition. This will discard your current display.</label>
            </v-card-text>
            <v-card-text class="px-4">
                <vu-input
                    v-model="page.import"
                    class="file-input mb-4"
                    density="compact"
                    hide-details="auto"
                    label="Display File"
                    placeholder="display.json5"
                    name="import"
                    show-size
                    type="file"
                    :clearable="true"
                    :multiple="false" />
            </v-card-text>
            <v-card-actions class="px-4 mb-4">
                <v-btn
                    :loading="page.importing"
                    ripple
                    color="primary"
                    type="submit"
                    variant="elevated"
                    @click="importDisplay">
                    Import
                </v-btn>
                <v-btn ripple variant="elevated" @click="page.showImport = false">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <vu-confirm ref="confirm" />
</template>

<style>
.display-list {
    .table-data {
        i {
            color: rgb(var(--v-theme-primary)) !important;
        }
    }
}
.import-dialog {
    .headline {
        background: rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-on-primary));
        font-weight: bold;
        padding: 16px;
    }
}
</style>
